import { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import Tooltip from '../../components/Tooltip/Tooltip';
import { isUserHasKeys } from '../../GlobalFunctions';
import { shouldShowLibrarySwitcher } from '../../Library/takweenLibraryAccess';
import { LockedContentMessage } from '../../LessonPlan/LockedContentMessage';
import { LockIcon } from '../../LessonPlan/LockIcon';
import { updateField } from '../../UserManagement';
import { updateUserProfile } from '../../BackendInterface';
import styles from '../../SCSS/Header.module.scss';
import middleSchoolStyles from './MiddleSchoolSubheader.module.scss';
import { History, Location } from 'history';
import { AppDispatch, RootState } from '../../ApplicationState';

const mapStateToProps = (state: RootState) => ({
  userProfile: state.user.userProfile,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  updateUserProfile: (field: string, value: unknown) => dispatch(updateField({ field, value })),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsFromRedux & {
  history: History;
  location: Location;
  isAuthenticated: boolean;
};

type State = {
  isShowLockedMessage: boolean;
  selected?: number;
};

const NewIconBadge = () => (
  <span
    style={{
      textTransform: 'uppercase',
      color: 'white',
      background: '#00afd8',
      borderRadius: '0.25rem',
      padding: '0.1rem 0.25rem',
      margin: '0',
      fontSize: '0.25rem',
      width: 'fit-content',
      position: 'relative',
      top: '-0.8rem',
      right: '-0.4rem',
      fontFamily: 'GT-Walsheim-Pro-Bold',
    }}
  >
    New
  </span>
);

class MiddleSchoolSubheader extends Component<Props, State> {
  onHideLockedMessage = () => {
    this.setState({
      isShowLockedMessage: false,
    });
  };
  getTabBadge = (headerIndex: number) => {
    const newBadge = this.getNewFunctionBadge(headerIndex);
    return newBadge;
  };
  isTabLocked = (headerIndex: number) => {
    if (
      this.menuOptions[headerIndex].requiredKeys &&
      !isUserHasKeys(this.menuOptions[headerIndex].requiredKeys) &&
      !shouldShowLibrarySwitcher()
    ) {
      return true;
    }

    return false;
  };

  getNewFunctionBadge = (headerIndex: number) => {
    const { createdAt, timestamps } = this.props.userProfile;
    const startDate = this.menuOptions[headerIndex].newOn;

    if (startDate) {
      if (startDate > createdAt) {
        const firstSeenDate = timestamps && timestamps.firstSawVideoLessons;

        if (firstSeenDate) {
          if (Date.now() - firstSeenDate < 1000 * 60 * 60 * 24 * 5) {
            return <NewIconBadge />;
          }
        } else {
          const newTimeStamp = Date.now();
          const newTimeStampsMap = {
            ...timestamps,
            firstSawVideoLessons: newTimeStamp,
          };
          this.props.updateUserProfile(timestamps, newTimeStampsMap);
          updateUserProfile({
            newUserData: {
              timestamps: newTimeStampsMap,
            },
          });
          return <NewIconBadge />;
        }
      }
    }

    return null;
  };

  constructor(props) {
    super(props);
    this.menuOptions = [
      {
        title: 'Library',
        route: '/library',
      },
      {
        title: 'Students',
        route: '/students',
      },
      {
        title: 'Check-In Tool',
        route: '/teacherLedCheckIn',
        requiredKeys: ['KEY_TEACHER_CHECK-IN'],
        isBeta: true,
      },
      {
        title: 'Classroom Settings',
        route: '/classroom',
      },
      {
        title: 'Reports',
        route: '/reports',
      },
    ];

    this.state = {
      selected: undefined,
      isShowLockedMessage: false,
    };
  }

  componentDidMount() {
    this.computeSelectedBasedOnRoute();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      this.computeSelectedBasedOnRoute();
    }
  }

  computeSelectedBasedOnRoute() {
    let newSelectedIndex;
    const pathname = this.props.location.pathname.toLowerCase();
    this.menuOptions.forEach((option) => {
      if (pathname.includes(option.route.toLowerCase())) {
        newSelectedIndex = this.mapTitleToIndex(option.title);
      }
    });
    this.setState({
      selected: newSelectedIndex,
    });
  }

  selectItem(itemIndex: number) {
    const saveSelected = this.state.selected;
    this.setState({
      selected: itemIndex,
    });

    if (this.menuOptions[itemIndex].requiredKeys) {
      if (!isUserHasKeys(this.menuOptions[itemIndex].requiredKeys) && !shouldShowLibrarySwitcher()) {
        this.setState({
          selected: saveSelected,
          isShowLockedMessage: true,
        });
        return;
      }
    }

    if (this.menuOptions[itemIndex].route) {
      this.props.history.push(this.menuOptions[itemIndex].route.toLowerCase());
    }
  }

  mapTitleToIndex(title: string) {
    const mapping: { [key: string]: number } = {
      Library: 0,
      Students: 1,
      'Check-In Tool': 2,
      'Classroom Settings': 3,
      Reports: 4,
    };
    return mapping[title];
  }

  render() {
    // console.debug(`%cSubheader render`, 'background: blue; color: yellow');

    if (!this.props.userProfile) {
      return <div className={styles.Subheader} />;
    }

    return (
      <div className={middleSchoolStyles.container}>
        <div className={styles.Subheader}>
          <div className={styles.subheaderContainer}>
            {this.menuOptions.map((option, index: number) => {
              const currentlySelectedTab = this.state.selected;
              const shouldShowBetaPill = option.title === 'Check-In Tool' && option.isBeta;
              const isCheckInLocked = shouldShowBetaPill && this.isTabLocked(index);
              return (
                <div
                  key={index}
                  className={`${styles.subheaderSection} ${currentlySelectedTab === index ? styles.selected : ''}`}
                  onClick={() => this.selectItem(index)}
                >
                  {shouldShowBetaPill ? (
                    <Tooltip
                      content='Conduct student check-ins directly from your device—no student login required. Results appear in the Moodboard report.'
                      direction='bottom'
                    >
                      <div className={styles.checkInLabel}>
                        <span>{option.title}</span>
                        <span className={`${styles.betaBadge} ${styles.checkInBetaBadge}`}>Beta</span>
                        {isCheckInLocked && (
                          <span className={styles.checkInLockBadge}>
                            <LockIcon
                              styleOverride={{
                                position: 'static',
                                top: 'auto',
                                right: 'auto',
                              }}
                            />
                          </span>
                        )}
                      </div>
                    </Tooltip>
                  ) : (
                    option.title
                  )}
                  {!shouldShowBetaPill && this.getTabBadge(index)}
                </div>
              );
            })}
          </div>
          <LockedContentMessage isShow={this.state.isShowLockedMessage} onHide={this.onHideLockedMessage} feature />
        </div>
      </div>
    );
  }
}

export default connector(MiddleSchoolSubheader);
